import React, { useEffect, useRef } from 'react';

export default function HiggsField({ mode }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const modeRef = useRef(mode);

  useEffect(() => { modeRef.current = mode; }, [mode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const COLS = 28, ROWS = 18;
    const pts = Array.from({ length: COLS * ROWS }, (_, i) => ({
      bx: (i % COLS) / (COLS - 1),
      by: Math.floor(i / COLS) / (ROWS - 1),
      phase: Math.random() * Math.PI * 2,
      freq: 0.4 + Math.random() * 0.3,
      amp: 0.006 + Math.random() * 0.008,
    }));

    const draw = (ts) => {
      const t = ts / 1000;
      const W = canvas.width = canvas.offsetWidth;
      const H = canvas.height = canvas.offsetHeight;
      const isRuach = modeRef.current === 'ruach';
      const nodeColor = isRuach ? '255,160,60' : '240,192,64';
      const lineColor = isRuach ? '255,140,40' : '240,192,64';

      ctx.clearRect(0, 0, W, H);

      pts.forEach(p => {
        const x = p.bx * W + Math.sin(t * p.freq + p.phase) * p.amp * W;
        const y = p.by * H + Math.cos(t * p.freq * 0.7 + p.phase) * p.amp * H;
        const strength = 0.5 + 0.5 * Math.sin(t * 0.3 + p.phase);
        ctx.beginPath();
        ctx.arc(x, y, 1.5 + strength * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${nodeColor},${0.06 + strength * 0.1})`;
        ctx.fill();
      });

      for (let i = 0; i < pts.length; i++) {
        const pi = pts[i];
        const xi = pi.bx * W + Math.sin(t * pi.freq + pi.phase) * pi.amp * W;
        const yi = pi.by * H + Math.cos(t * pi.freq * 0.7 + pi.phase) * pi.amp * H;
        for (let j = i + 1; j < pts.length; j++) {
          const pj = pts[j];
          const xj = pj.bx * W + Math.sin(t * pj.freq + pj.phase) * pj.amp * W;
          const yj = pj.by * H + Math.cos(t * pj.freq * 0.7 + pj.phase) * pj.amp * H;
          const dx = xi - xj, dy = yi - yj;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxD = W / (COLS * 0.85);
          if (dist < maxD) {
            ctx.beginPath();
            ctx.moveTo(xi, yi);
            ctx.lineTo(xj, yj);
            ctx.strokeStyle = `rgba(${lineColor},${(1 - dist / maxD) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <canvas ref={canvasRef} style={{
      position: 'fixed', inset: 0, width: '100%', height: '100%',
      pointerEvents: 'none', zIndex: 0,
    }} />
  );
}
